import { Stack } from "@mui/material";
import React, { useMemo } from "react";
import {
  disclosedRecommendationsForDataRecipient,
  recommendationsForDataRecipient,
} from "../../../common/service/resources/data-map/dataMapHelpers";
import { useDataMapCollectedPIC } from "../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto, PICGroup } from "../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../common/service/server/Types";
import { UpdateDisclosedArgs } from "../../hooks/useDisclosuresForCollectionGroups";
import { DESCRIPTION_OVERRIDES } from "../../pages/get-compliant/data-recipients/DataRecipientCollectionCheckboxes";
import { DescriptionCheckbox } from "../input/DescriptionCheckbox";
import { RecipientGuidance } from "./data-recipients/RecipientGuidance";

type DataRecipientDisclosureCheckboxesProps = {
  updateDisclosure: (params: UpdateDisclosedArgs) => void;
  collectionGroups: CollectionGroupDetailsDto[];
  collectedPicGroups: PICGroup[];
  selected: UUIDString[];
  pending: UUIDString[];
  showRecommendations?: boolean;
  dataMap?: DataMapDto | undefined;
  dataRecipient: OrganizationDataRecipientDto;
  showCollectedRecommendations?: boolean;
  showSensitiveCategories?: boolean;
};

export const DataRecipientDisclosureCheckboxes: React.FC<
  DataRecipientDisclosureCheckboxesProps
> = ({
  updateDisclosure,
  dataMap,
  collectionGroups,
  collectedPicGroups,
  selected,
  pending,
  dataRecipient,
  showRecommendations = false,
  showCollectedRecommendations = false,
  showSensitiveCategories = false,
}) => {
  const { recommendedGroups } = useMemo(() => {
    if (dataMap && showRecommendations) {
      return recommendationsForDataRecipient(dataMap, dataRecipient);
    } else if (dataMap && showCollectedRecommendations) {
      return disclosedRecommendationsForDataRecipient(dataMap, dataRecipient, collectionGroups);
    }
    return { recommendedGroups: [] };
  }, [dataMap, dataRecipient, showRecommendations, showCollectedRecommendations]);

  const handleOnChange = (disclosedId: UUIDString, disclosed: boolean) => {
    updateDisclosure({
      vendorId: dataRecipient.vendorId,
      disclosedGroupIds: [disclosedId],
      disclosed,
    });
  };

  return (
    <>
      <RecipientGuidance recipient={dataRecipient} />
      <Stack spacing={2}>
        {collectedPicGroups.map((picGroup) => (
          <DataRecipientDisclosureCheckbox
            key={picGroup.id}
            dataMap={dataMap}
            collectionGroups={collectionGroups}
            picGroup={picGroup}
            selected={selected}
            pending={pending}
            recommendedGroups={recommendedGroups}
            onChange={handleOnChange}
            showSensitiveCategories={showSensitiveCategories}
          />
        ))}
      </Stack>
    </>
  );
};

const DataRecipientDisclosureCheckbox = ({
  picGroup,
  collectionGroups,
  dataMap,
  selected,
  pending,
  recommendedGroups,
  onChange,
  showSensitiveCategories = false,
}: {
  dataMap: DataMapDto | undefined;
  collectionGroups: CollectionGroupDetailsDto[];
  picGroup: PICGroup;
  selected: UUIDString[];
  pending: UUIDString[];
  recommendedGroups: PICGroup[];
  onChange: (disclosedId: UUIDString, disclosed: boolean) => void;
  showSensitiveCategories?: boolean;
}) => {
  const cgIds = collectionGroups.map((cg) => cg.id);
  const collectedPic = useDataMapCollectedPIC(dataMap, {
    collectionGroupId: cgIds,
    picGroupId: picGroup.id,
  });
  const collectedLabel = collectedPic.map((c) => c.name).join(", ");
  // Check if any collected PIC is sensitive in any region
  const containsSensitivePic = collectedPic.some((c) => c.sensitiveRegions?.length > 0);
  const description = DESCRIPTION_OVERRIDES[picGroup.key] ?? `You collect: ${collectedLabel}`;
  const recommended = recommendedGroups?.some((rg) => rg.id === picGroup.id);

  return (
    <DescriptionCheckbox
      label={
        <>
          {picGroup.name}
          {showSensitiveCategories && containsSensitivePic && (
            <span className="ml-xs" style={{ color: "#d32f2f", fontWeight: "bold" }}>
              *
            </span>
          )}
        </>
      }
      description={description}
      checked={selected?.includes(picGroup.id)}
      variant={recommended ? "light-purple" : undefined}
      loading={pending?.includes(picGroup.id)}
      onChange={(_, checked) => onChange(picGroup.id, checked)}
    />
  );
};
